import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Paperclip, Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { FORMS } from "./formConfigs";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(fields, values) {
  const errs = {};
  fields.forEach((f) => {
    if (!f.name || (f.showIf && !f.showIf(values))) return;
    const v = (values[f.name] || "").trim();
    const req = f.required || (f.requiredIf && f.requiredIf(values));
    if (req && !v) errs[f.name] = `${f.label} is required`;
    else if (v && f.type === "email" && !EMAIL_RX.test(v)) errs[f.name] = "Enter a valid email address, like name@example.com";
    else if (v && f.pattern && !f.pattern.test(v)) errs[f.name] = f.patternMsg;
  });
  if (!values.consent) errs.consent = "Please tick the box to agree to the privacy collection notice";
  return errs;
}

function Field({ f, value, error, onChange, prefix }) {
  const id = `${prefix}-${f.name}`;
  const describedBy = [f.hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;
  const common = { id, name: f.name, "aria-invalid": error ? "true" : "false", "aria-describedby": describedBy, "data-testid": `${prefix}-field-${f.name}` };
  const req = f.required || (f.requiredIf && f.requiredIf({}));
  const label = (
    <>
      {f.label}
      {f.required && <span className="text-danger" aria-hidden="true"> *</span>}
      {f.required && <span className="sr-only"> (required)</span>}
    </>
  );
  const hint = f.hint && <p id={`${id}-hint`} className="mt-1 text-[0.95rem] text-ink-muted">{f.hint}</p>;
  const err = error && (
    <p id={`${id}-error`} className="mt-2 flex items-center gap-2 font-semibold text-danger"><AlertCircle size={18} aria-hidden="true" />{error}</p>
  );

  if (f.type === "radio") {
    return (
      <fieldset className="sm:col-span-2" aria-describedby={describedBy} aria-invalid={error ? "true" : "false"} id={id} tabIndex={-1}>
        <legend className="font-semibold">{label}</legend>
        {hint}
        <div className="mt-3 flex flex-wrap gap-3">
          {f.options.map((o) => (
            <label key={o} className={`flex min-h-[52px] cursor-pointer items-center gap-3 rounded-xl border-2 px-4 py-2 transition-colors ${value === o ? "border-teal bg-teal-soft" : "border-line bg-surface hover:border-teal"}`}>
              <input type="radio" name={f.name} value={o} checked={value === o} onChange={(e) => onChange(f.name, e.target.value)} className="h-5 w-5 accent-[rgb(var(--c-teal))]" data-testid={`${prefix}-radio-${f.name}-${o.split(" ")[0].toLowerCase()}`} />
              <span>{o}</span>
            </label>
          ))}
        </div>
        {err}
      </fieldset>
    );
  }
  return (
    <div className={f.half ? "" : "sm:col-span-2"}>
      <label htmlFor={id} className="font-semibold">{label}</label>
      {hint}
      {f.type === "textarea" ? (
        <textarea {...common} rows={5} value={value} onChange={(e) => onChange(f.name, e.target.value)} className="field-input" required={req} />
      ) : f.type === "select" ? (
        <select {...common} value={value} onChange={(e) => onChange(f.name, e.target.value)} className="field-input" required={req}>
          <option value="">Select an option</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input {...common} type={f.type || "text"} value={value} autoComplete={f.autoComplete} inputMode={f.inputMode}
          onChange={(e) => onChange(f.name, e.target.value)} className="field-input" required={req} />
      )}
      {err}
    </div>
  );
}

export default function DynamicForm({ kind, initial = {}, testid }) {
  const cfg = FORMS[kind];
  const prefix = testid || `${kind}-form`;
  const [values, setValues] = useState(() => ({ consent: false, website: "", ...initial }));
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle" });
  const summaryRef = useRef(null);
  const successRef = useRef(null);

  const onChange = (name, v) => {
    setValues((s) => ({ ...s, [name]: v }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(cfg.fields, values);
    if (cfg.files && files.length > cfg.files.max) errs.files = `Please attach no more than ${cfg.files.max} files`;
    if (files.some((f) => f.size > 10 * 1024 * 1024)) errs.files = "Each file must be smaller than 10 MB";
    setErrors(errs);
    if (Object.keys(errs).length) {
      setStatus({ state: "invalid" });
      setTimeout(() => summaryRef.current?.focus(), 0);
      return;
    }
    setStatus({ state: "sending" });
    const fd = new FormData();
    fd.append("data", JSON.stringify(values));
    files.forEach((f) => fd.append("files", f));
    try {
      const r = await api.post(`/forms/${kind}`, fd);
      setStatus({ state: "done", ref: r.data.ref });
      setTimeout(() => successRef.current?.focus(), 0);
    } catch (err) {
      setStatus({ state: "error", message: errMsg(err) });
      setTimeout(() => summaryRef.current?.focus(), 0);
    }
  };

  if (status.state === "done") {
    return (
      <div ref={successRef} tabIndex={-1} role="status" className="card border-2 border-success p-8" data-testid={`${prefix}-success`}>
        <CheckCircle2 className="text-success" size={40} aria-hidden="true" />
        <h3 className="mt-4 font-display text-2xl font-semibold">Thank you, we've received your request</h3>
        <p className="mt-3 text-lg">Your reference number is <strong data-testid={`${prefix}-ref`}>{status.ref}</strong>. We've sent a confirmation to your email, and a member of our team will be in touch soon.</p>
      </div>
    );
  }

  const errList = Object.entries(errors).filter(([, v]) => v);
  return (
    <form onSubmit={onSubmit} noValidate className="card p-6 sm:p-8" aria-label={cfg.submitLabel} data-testid={prefix}>
      <p className="mb-6 text-ink-muted">Fields marked with <span className="text-danger" aria-hidden="true">*</span><span className="sr-only">an asterisk</span> are required.</p>
      {(status.state === "invalid" || status.state === "error") && (
        <div ref={summaryRef} tabIndex={-1} role="alert" className="mb-6 rounded-xl border-2 border-danger bg-coral-soft p-5" data-testid={`${prefix}-error-summary`}>
          <h3 className="font-display text-lg font-semibold text-danger">{status.state === "error" ? "We couldn't send your form" : "Please fix the following"}</h3>
          {status.state === "error" ? <p className="mt-2">{status.message}</p> : (
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {errList.map(([k, v]) => <li key={k}><a className="underline" href={`#${prefix}-${k}`}>{v}</a></li>)}
            </ul>
          )}
        </div>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        {cfg.fields.map((f, i) => {
          if (f.type === "heading") return <h3 key={i} className="border-b border-line pb-2 pt-2 font-display text-xl font-semibold sm:col-span-2">{f.label}</h3>;
          if (f.showIf && !f.showIf(values)) return null;
          return <Field key={f.name} f={f} value={values[f.name] || ""} error={errors[f.name]} onChange={onChange} prefix={prefix} />;
        })}
        {cfg.files && (
          <div className="sm:col-span-2">
            <label htmlFor={`${prefix}-files`} className="font-semibold">{cfg.files.label}</label>
            <p id={`${prefix}-files-hint`} className="mt-1 text-[0.95rem] text-ink-muted">{cfg.files.hint}</p>
            <input id={`${prefix}-files`} type="file" multiple accept={cfg.files.accept} aria-describedby={`${prefix}-files-hint`}
              onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid={`${prefix}-files`}
              className="field-input file:mr-4 file:rounded-lg file:border-0 file:bg-teal-soft file:px-4 file:py-2 file:font-semibold file:text-teal" />
            {files.length > 0 && (
              <ul className="mt-3 space-y-1 text-[0.95rem]">{files.map((f) => <li key={f.name} className="flex items-center gap-2"><Paperclip size={16} aria-hidden="true" />{f.name}</li>)}</ul>
            )}
            {errors.files && <p className="mt-2 font-semibold text-danger">{errors.files}</p>}
          </div>
        )}
        <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
          <label htmlFor={`${prefix}-website`}>Leave this field empty</label>
          <input id={`${prefix}-website`} name="website" tabIndex={-1} autoComplete="off" value={values.website} onChange={(e) => onChange("website", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-sand p-4">
            <input id={`${prefix}-consent`} type="checkbox" checked={values.consent} onChange={(e) => onChange("consent", e.target.checked)}
              aria-invalid={errors.consent ? "true" : "false"} aria-describedby={errors.consent ? `${prefix}-consent-error` : undefined}
              className="mt-1 h-6 w-6 shrink-0 accent-[rgb(var(--c-teal))]" data-testid={`${prefix}-consent`} />
            <span>{cfg.consent} <Link to="/privacy" className="font-semibold text-teal underline">Read our Privacy Policy</Link>.<span className="text-danger" aria-hidden="true"> *</span></span>
          </label>
          {errors.consent && <p id={`${prefix}-consent-error`} className="mt-2 font-semibold text-danger">{errors.consent}</p>}
        </div>
      </div>
      <button type="submit" className="btn-primary mt-8 w-full sm:w-auto" disabled={status.state === "sending"} data-testid={`${prefix}-submit`}>
        {status.state === "sending" && <Loader2 className="animate-spin" size={20} aria-hidden="true" />}
        {status.state === "sending" ? "Sending…" : cfg.submitLabel}
      </button>
      <p className="sr-only" aria-live="polite">{status.state === "sending" ? "Sending your form" : ""}</p>
    </form>
  );
}
